/**
 * @fileoverview DashboardStats - Display dashboard statistics in a refined 4-column grid
 * Shows key metrics: Active Runs, Active Sessions, Queued, Failed Today
 * Each card has an icon in a colored circle, large value, and descriptive sublabel.
 */
import { Link } from 'react-router-dom'
import { Activity, MessageSquare, Clock, XCircle, TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

export interface DashboardStatsData {
  activeRuns: number
  activeSessions: number
  totalProjects: number
  queuedRuns: number
  failedToday: number
  totalTokens: number
}

export interface DashboardStatsProps {
  stats: DashboardStatsData
  isLoading?: boolean
}

type StatVariant = 'default' | 'primary' | 'success' | 'warning' | 'error'

interface StatCardItemProps {
  label: string
  value: number
  sublabel: string
  icon: React.ElementType
  variant?: StatVariant
  trend?: 'up' | 'down' | 'flat'
  linkTo?: string
}

function formatNumber(num: number): string {
  return num.toLocaleString()
}

const iconBgClasses: Record<StatVariant, string> = {
  default: 'bg-primary/10 text-primary',
  primary: 'bg-primary/10 text-primary',
  success: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
  warning: 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400',
  error: 'bg-destructive/10 text-destructive',
}

const valueClasses: Record<StatVariant, string> = {
  default: '',
  primary: 'text-primary',
  success: '',
  warning: '',
  error: 'text-destructive',
}

function TrendIndicator({ trend }: { trend: 'up' | 'down' | 'flat' }) {
  if (trend === 'up') {
    return <TrendingUp className="h-3.5 w-3.5 text-green-500" />
  }
  if (trend === 'down') {
    return <TrendingDown className="h-3.5 w-3.5 text-red-500" />
  }
  return <Minus className="h-3.5 w-3.5 text-muted-foreground" />
}

function StatCardItem({ label, value, sublabel, icon: Icon, variant = 'default', trend, linkTo }: StatCardItemProps) {
  const card = (
    <Card
      data-stat-card
      data-variant={variant}
      className={cn(
        'relative overflow-hidden h-full transition-colors',
        variant === 'error' && value > 0 && 'border-destructive/30 bg-destructive/5',
        linkTo && 'hover:bg-accent/50 cursor-pointer'
      )}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className={cn('p-2.5 rounded-xl flex-shrink-0', iconBgClasses[variant === 'error' && value > 0 ? 'error' : variant])}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0 text-right">
            <p className="text-sm font-medium text-muted-foreground truncate">{label}</p>
            <div className="flex items-baseline justify-end gap-1.5 mt-1">
              <p className={cn('text-3xl font-bold tracking-tight', valueClasses[variant === 'error' && value > 0 ? 'error' : variant])}>
                {formatNumber(value)}
              </p>
              {trend && <TrendIndicator trend={trend} />}
            </div>
            <p className="text-xs text-muted-foreground mt-1 truncate">{sublabel}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  )

  if (linkTo) {
    return <Link to={linkTo} className="block h-full">{card}</Link>
  }
  return card
}

function StatSkeleton() {
  return (
    <Card data-testid="stat-skeleton">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <Skeleton className="h-10 w-10 rounded-xl" />
          <div className="space-y-2 text-right">
            <Skeleton className="h-4 w-20 ml-auto" />
            <Skeleton className="h-8 w-14 ml-auto" />
            <Skeleton className="h-3 w-24 ml-auto" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export function DashboardStats({ stats, isLoading }: DashboardStatsProps) {
  if (isLoading) {
    return (
      <div
        className="grid grid-cols-2 lg:grid-cols-4 gap-4"
        role="region"
        aria-label="Dashboard statistics"
        aria-busy="true"
      >
        {Array.from({ length: 4 }).map((_, i) => (
          <StatSkeleton key={i} />
        ))}
      </div>
    )
  }

  return (
    <div
      className="grid grid-cols-2 lg:grid-cols-4 gap-4"
      role="region"
      aria-label="Dashboard statistics"
    >
      <StatCardItem
        label="Active Runs"
        value={stats.activeRuns}
        sublabel="Currently running"
        icon={Activity}
        variant="primary"
      />
      <StatCardItem
        label="Active Sessions"
        value={stats.activeSessions}
        sublabel="Open conversations"
        icon={MessageSquare}
      />
      <StatCardItem
        label="Queued"
        value={stats.queuedRuns}
        sublabel="Waiting to start"
        icon={Clock}
        variant="warning"
      />
      <StatCardItem
        label="Failed Today"
        value={stats.failedToday}
        sublabel="Require attention"
        icon={XCircle}
        variant="error"
      />
    </div>
  )
}
